
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Users, CheckSquare, Calendar, DollarSign, Plus, Info, Clock } from 'lucide-react';
import { useScoutAssignments } from '@/hooks/useScoutAssignments';
import { useAuth } from '@/contexts/AuthContext';
import RequestAssignmentsModal from '@/components/scout/RequestAssignmentsModal';
import AthleteActionDrawer from '@/components/scout/AthleteActionDrawer';
import { Link, useNavigate } from 'react-router-dom';

interface AssignedAthlete {
  id: string;
  username: string;
  email: string;
  sport_id: string;
  grad_year?: number;
  avatar_url?: string;
  assigned_at: string;
}

const ScoutDashboardHome = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { assignedAthletes, scoutTasks, pendingRequests, isLoading, requestAthleteAccess } = useScoutAssignments();
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [selectedAthlete, setSelectedAthlete] = useState<AssignedAthlete | null>(null);
  const [isAthleteDrawerOpen, setIsAthleteDrawerOpen] = useState(false);

  const handleInfoClick = (e: React.MouseEvent, athlete: AssignedAthlete) => {
    e.stopPropagation();
    setSelectedAthlete(athlete);
    setIsAthleteDrawerOpen(true);
  };

  const closeAthleteDrawer = () => {
    setIsAthleteDrawerOpen(false);
    setSelectedAthlete(null);
  };

  const handleViewProfile = (athleteId: string) => {
    console.log('🔍 Scout navigating to athlete profile:', athleteId);
    // Navigate to the main athlete dashboard, not profile subpage
    const targetPath = `/athlete-dashboard?profile_id=${athleteId}&asScout=1`;
    console.log('🔍 Target path:', targetPath);
    navigate(targetPath);
  };

  // Calculate real stats from live data
  const stats = [
    {
      title: "My Athletes",
      value: assignedAthletes.length.toString(),
      description: "Athletes connected to you",
      icon: Users,
      color: "text-blue-600"
    },
    {
      title: "Active Tasks",
      value: scoutTasks.filter(task => task.status !== 'completed').length.toString(),
      description: "Pending recruiting tasks",
      icon: CheckSquare,
      color: "text-green-600"
    },
    {
      title: "Completed Tasks",
      value: scoutTasks.filter(task => task.status === 'completed').length.toString(),
      description: "Tasks completed this month",
      icon: Calendar,
      color: "text-purple-600"
    },
    {
      title: "High Priority",
      value: scoutTasks.filter(task => task.priority === 'high' && task.status !== 'completed').length.toString(),
      description: "Urgent tasks requiring attention",
      icon: DollarSign,
      color: "text-red-600"
    }
  ];

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Scout Dashboard</h1>
          <p className="text-muted-foreground">Loading your scout assignments...</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="space-y-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-6 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-4 bg-muted rounded w-full"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Scout Dashboard</h1>
        <p className="text-sm md:text-base text-muted-foreground">
          Welcome back, {profile?.name}. Manage your connected athletes and recruiting activities.
        </p>
      </div>

      {/* Stats Cards - Stack on mobile, grid on larger screens */}
      <div className="grid gap-3 md:gap-4 grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const IconComponent = stat.icon;
          return (
            <Card key={stat.title} className="p-3 md:p-0">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 md:p-6 md:pb-2">
                <CardTitle className="text-xs md:text-sm font-medium leading-tight">
                  {stat.title}
                </CardTitle>
                <IconComponent className={`h-3 w-3 md:h-4 md:w-4 ${stat.color} flex-shrink-0`} />
              </CardHeader>
              <CardContent className="p-3 md:p-6 pt-0">
                <div className="text-lg md:text-2xl font-bold">{stat.value}</div>
                <p className="text-xs text-muted-foreground leading-tight">
                  {stat.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main Content - Stack on mobile, side by side on larger screens */}
      <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>My Athletes</CardTitle>
            <CardDescription>
              Athletes currently connected to you
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pendingRequests.length > 0 && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Clock className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">
                    Pending Requests ({pendingRequests.length})
                  </span>
                </div>
                <div className="space-y-2">
                  {pendingRequests.slice(0, 3).map((request) => (
                    <div key={request.id} className="text-xs text-yellow-700">
                      {request.athlete_email} - {new Date(request.requested_at).toLocaleDateString()}
                    </div>
                  ))}
                  {pendingRequests.length > 3 && (
                    <div className="text-xs text-yellow-600">
                      +{pendingRequests.length - 3} more pending...
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {assignedAthletes.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Athletes Connected</h3>
                <p className="text-muted-foreground mb-4">
                  You don't have any athletes connected to you yet.
                </p>
                <Button variant="outline" size="sm" onClick={() => setIsRequestModalOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Connect to Athletes
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {assignedAthletes.slice(0, 4).map((athlete) => (
                   <div 
                     key={athlete.id} 
                     className="p-3 md:p-4 border rounded-lg hover:shadow-md hover:border-blue-200 transition-all"
                   >
                     <div className="flex items-start md:items-center space-x-3">
                       <Avatar className="h-10 w-10 md:h-12 md:w-12 flex-shrink-0">
                         <AvatarImage src={athlete.avatar_url} alt={athlete.username} />
                         <AvatarFallback className="bg-blue-100 text-blue-600 text-sm">
                           {athlete.username.charAt(0).toUpperCase()}
                         </AvatarFallback>
                       </Avatar>
                       <div className="flex-1 min-w-0">
                         <div className="space-y-2">
                           {/* Name and basic info */}
                           <div>
                             <h4 className="font-medium text-gray-900 truncate text-sm md:text-base">
                               {athlete.username}
                             </h4>
                             <p className="text-xs md:text-sm text-muted-foreground truncate">
                               {athlete.email}
                             </p>
                           </div>
                           
                           {/* Sport and graduation info */}
                           <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                             <span>Sport ID: {athlete.sport_id}</span>
                             {athlete.grad_year && (
                               <>
                                 <span>•</span>
                                 <span>Class of {athlete.grad_year}</span>
                               </>
                             )}
                           </div>
                           
                           {/* Actions and status - Stack on mobile */}
                           <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                             <div className="flex items-center space-x-2">
                               <Badge variant="default" className="text-xs">
                                 Connected
                               </Badge>
                               <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                                 <Clock className="h-3 w-3" />
                                 <span className="hidden sm:inline">
                                   {new Date(athlete.assigned_at).toLocaleDateString()}
                                 </span>
                                 <span className="sm:hidden">
                                   {new Date(athlete.assigned_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                 </span>
                               </div>
                             </div>
                             
                             <div className="flex items-center space-x-2">
                               <Button
                                 onClick={() => handleViewProfile(athlete.id)}
                                 size="sm"
                                 className="bg-green-600 hover:bg-green-700 text-white transition-colors text-xs px-2 md:px-3 py-1 min-h-[32px]"
                               >
                                 <span className="hidden sm:inline">View Profile</span>
                                 <span className="sm:hidden">View</span>
                               </Button>
                               <Badge 
                                 variant="secondary" 
                                 className="text-xs cursor-pointer hover:bg-gray-200 flex items-center gap-1 min-h-[32px] px-2"
                                 onClick={(e) => handleInfoClick(e, athlete)}
                               >
                                 <Info className="h-3 w-3" />
                                 <span className="hidden sm:inline">Info</span>
                               </Badge>
                             </div>
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>
                ))}
                
                <div className="flex space-x-2 pt-2">
                  {assignedAthletes.length > 4 && (
                    <Button variant="outline" className="flex-1" asChild>
                      <Link to="/scout-dashboard/athletes">
                        View All Athletes ({assignedAthletes.length})
                      </Link>
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setIsRequestModalOpen(true)}
                    className={assignedAthletes.length > 4 ? "" : "flex-1"}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Connect More
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Tasks</CardTitle>
            <CardDescription>
              Your latest recruiting activities and tasks
            </CardDescription>
          </CardHeader>
          <CardContent>
            {scoutTasks.length === 0 ? (
              <div className="text-center py-8">
                <CheckSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Active Tasks</h3>
                <p className="text-muted-foreground mb-4">
                  You're all caught up! Create a new task to get started.
                </p>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/scout-dashboard/tasks">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Task
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {scoutTasks.slice(0, 5).map((task) => (
                  <div key={task.id} className="flex items-center space-x-4">
                    <div className={`w-2 h-2 rounded-full ${
                      task.priority === 'high' ? 'bg-red-500' :
                      task.priority === 'normal' ? 'bg-yellow-500' : 'bg-green-500'
                    }`}></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{task.description || 'Untitled Task'}</p>
                      <p className="text-xs text-muted-foreground">
                        {task.athlete_username ? `For ${task.athlete_username}` : 'General task'}
                      </p>
                    </div>
                    <Badge variant={
                      task.status === 'completed' ? 'default' :
                      task.status === 'in_progress' ? 'secondary' : 'outline'
                    } className="text-xs">
                      {task.status.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
                {scoutTasks.length > 5 && (
                  <Button variant="outline" className="w-full" asChild>
                    <Link to="/scout-dashboard/tasks">
                      View All Tasks ({scoutTasks.length})
                    </Link>
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common tasks and shortcuts for scouts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <Button variant="outline" className="h-auto p-4 justify-start" asChild>
              <Link to="/scout-dashboard/athletes">
                <div className="flex items-center space-x-3">
                  <Users className="h-5 w-5 text-blue-600" />
                  <div className="text-left">
                    <p className="font-medium">View Athletes</p>
                    <p className="text-sm text-muted-foreground">Manage assigned athletes</p>
                  </div>
                </div>
              </Link>
            </Button>
            
            <Button variant="outline" className="h-auto p-4 justify-start" asChild>
              <Link to="/scout-dashboard/tasks">
                <div className="flex items-center space-x-3">
                  <CheckSquare className="h-5 w-5 text-green-600" />
                  <div className="text-left">
                    <p className="font-medium">Manage Tasks</p>
                    <p className="text-sm text-muted-foreground">Create and track tasks</p>
                  </div>
                </div>
              </Link>
            </Button>
            
            <Button variant="outline" className="h-auto p-4 justify-start" asChild>
              <Link to="/scout-dashboard/calendar">
                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-orange-600" />
                  <div className="text-left">
                    <p className="font-medium">View Calendar</p>
                    <p className="text-sm text-muted-foreground">Upcoming events and deadlines</p>
                  </div>
                </div>
              </Link>
            </Button>

            <Button variant="outline" className="h-auto p-4 justify-start" asChild>
              <Link to="/scout-dashboard/finance">
                <div className="flex items-center space-x-3">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  <div className="text-left">
                    <p className="font-medium">Finance</p>
                    <p className="text-sm text-muted-foreground">Track commission earnings</p>
                  </div>
                </div>
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <RequestAssignmentsModal
        isOpen={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        onSubmit={requestAthleteAccess}
      />

      <AthleteActionDrawer
        athlete={selectedAthlete}
        isOpen={isAthleteDrawerOpen}
        onClose={closeAthleteDrawer}
      />
    </div>
  );
};

export default ScoutDashboardHome;
